import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import HreflangMeta from '../components/HreflangMeta';
import articles from '../data/blog-data.json';
import './BlogArticle.css';

const BASE_URL = 'https://libbenagents.com';

function formatDate(dateStr, locale) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString(locale === 'en' ? 'en-US' : 'pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function renderContent(content) {
  const paragraphs = content.split('\n\n');
  return paragraphs.map((paragraph, index) => {
    // Render ## headings as h2
    if (paragraph.startsWith('## ')) {
      return (
        <h2 key={index} className="blog-article-heading">
          {paragraph.replace('## ', '')}
        </h2>
      );
    }
    return (
      <p key={index} className="blog-article-paragraph">
        {paragraph}
      </p>
    );
  });
}

export default function BlogArticle() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en' : 'pt';

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="blog-article-page">
        <HreflangMeta />
        <div className="blog-article-not-found">
          <h1>{t('blog.articleNotFoundTitle')}</h1>
          <p>{t('blog.articleNotFoundText')}</p>
          <Link to="/blog" className="blog-article-not-found-cta">
            {t('blog.articleNotFoundCta')}
          </Link>
        </div>
      </div>
    );
  }

  const localized = article[locale];
  const articleUrl = `${BASE_URL}/blog/${article.slug}`;
  const ogImageUrl = article.ogImage ? `${BASE_URL}${article.ogImage}` : undefined;

  return (
    <div className="blog-article-page">
      <Helmet>
        <title>{localized.title} — Libben Agents</title>
        <meta name="description" content={localized.description} />
        <meta property="og:title" content={localized.title} />
        <meta property="og:description" content={localized.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={articleUrl} />
        {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
      </Helmet>
      <HreflangMeta />

      <article className="blog-article-container">
        <Link to="/blog" className="blog-article-back">
          {t('blog.backToBlog')}
        </Link>

        <header className="blog-article-header">
          <span className="blog-article-date">
            {formatDate(article.publishedAt, locale)}
          </span>
          <h1 className="blog-article-title">{localized.title}</h1>
          <p className="blog-article-description">{localized.description}</p>
        </header>

        {article.ogImage && (
          <div className="blog-article-image">
            <img src={article.ogImage} alt={localized.title} />
          </div>
        )}

        <div className="blog-article-body">{renderContent(localized.content)}</div>

        <aside className="blog-article-agent-cta">
          <span className="blog-article-agent-label">
            {t('blog.relatedAgent')}
          </span>
          <Link to="/agentes" className="blog-article-agent-link">
            {t('blog.seeAgents', { defaultValue: 'Ver agentes disponíveis' })}
          </Link>
        </aside>
      </article>
    </div>
  );
}
